<template>
  <div class="commutator-details w-100">
    <accordion-panel
      class="mb-4 mb-xl-0 avoid-break"
      :collapsed.sync="collapsed"
      :id="`${commutatorId}`"
    >
      <template #title>
        <div class="commutator-details__title-container w-100">
          <b-dropdown
            v-show="$hasUserPermission('PER013') && hasCommutator"
            class="commutator-details__options-btn"
            left
            no-caret
            offset="-5"
            variant="link"
            boundary="window"
          >
            <template slot="button-content">
              <i
                class="fas fa-ellipsis-v text-gray"
                aria-hidden="true"
              />
            </template>
            <b-dropdown-item @click="informMaintenance">
              {{$t('engineering.commutator-maintenance.inform-maintenance')}}
            </b-dropdown-item>
          </b-dropdown>
          <div class="commutator-details__maintenance-icon">
            <i
              v-show="commutator.hasMaintenanceSuggestion"
              :id="`maintenance-suggestion-${commutatorId}`"
              class="fas fa-exclamation-triangle text-red mr-2"
              aria-hidden="true"
            />
            <b-tooltip :target="`maintenance-suggestion-${commutatorId}`">
              <span v-html="getMaintenanceSuggestionTooltipMessage()" />
              <span
                class="commutator-details__card-tooltip-link"
                @click="openPossibleMaintenanceModal"
              >
                {{ $t('general.click-here') }}
              </span>.
            </b-tooltip>
          </div>
          <div class="commutator-details__title">
            <strong>
              {{ $t('general.commutator', {CommutatorNumber: commutatorId }) }}
            </strong>
          </div>
          <div class="commutator-details__maintenance-date">
            <strong>{{ $t('engineering.commutator-maintenance.last-maintenance-date' )}}: </strong>
            <date-time-label
              v-if="commutator.lastMaintenanceDate"
              :value="commutator.lastMaintenanceDate"
              :format="dateLabelFormat"
            />
            <span
              v-else
              class="text-green"
            > -
            </span>
          </div>
        </div>
      </template>
      <div class="commutator-details__body">
        <b-row no-gutters>
          <b-col
            class="commutator-details__body-maintenance"
            cols="4"
          >
            <next-maintenance-section :next-maintenance="nextMaintenance" />
          </b-col>
          <b-col
            class="commutator-details__body-references pl-2"
            cols="8"
          >
            <references-section :references="references" />
          </b-col>
        </b-row>
      </div>
    </accordion-panel>
    <possible-maintenance-modal
      v-model="showPossibleMaintenanceModal"
      :commutator-id="commutatorId"
      @refresh-data="refreshData"
    />
  </div>
</template>

<script>
import AccordionPanel from '@/components/AccordionPanel';
import DateTimeLabel from '@/components/globals/DateTimeLabel';
import PossibleMaintenanceModal
  from '@/domain/components/installation/engineering/commutatorMaintenance/modal/PossibleMaintenanceModal';
import { formats } from '@/constants/localizer';
import NextMaintenanceSection from './NextMaintenanceSection';
import ReferencesSection from './ReferencesSection';

export default {
  name: 'CommutatorDetails',
  components: {
    PossibleMaintenanceModal,
    AccordionPanel,
    DateTimeLabel,
    NextMaintenanceSection,
    ReferencesSection,
  },
  props: {
    commutator: {
      type: Object,
      required: true,
    },
    commutatorId: {
      type: Number,
      required: true,
    },
  },
  computed: {
    hasCommutator() {
      return this.commutator.commutatorId;
    },
    references() {
      return this.commutator.references || {};
    },
    nextMaintenance() {
      return this.commutator.nextMaintenance || {};
    },
  },
  data: () => ({
    collapsed: false,
    dateLabelFormat: formats.DATE_SHORT,
    showPossibleMaintenanceModal: false,
  }),
  methods: {
    informMaintenance() {
      this.$emit('inform-maintenance', this.commutator.commutatorId);
    },
    getMaintenanceSuggestionTooltipMessage() {
      const message = this.$t('messages.MI027.message');
      const messageWithoutClickHere = message.split(' ').slice(0, -2).join(' ');
      return `<strong>${this.$t('messages.MI027.title')}:</strong> ${messageWithoutClickHere}`;
    },
    openPossibleMaintenanceModal() {
      this.showPossibleMaintenanceModal = true;
    },
    refreshData() {
      this.$emit('refresh-data');
    },
  },
};
</script>
<style lang="sass" scoped>
  @import "src/styles/defaults/mobile-first"
  @import "src/styles/defaults/variables"

  .commutator-details
    &__title-container
      display: flex
      align-items: center
      +media-max-sm
        flex-wrap: wrap
    &__title
      min-width: 20vw
    &__maintenance-icon
      min-width: 1.975vw
      z-index: 1
    &__maintenance-date
      min-width:  30vw
    &__card-tooltip-link
      color: $color-link
      text-decoration: underline
      cursor: pointer
      display: inline-block
    &__options-btn
      z-index: 2
    ::v-deep
      .accordion-panel
        box-shadow: none
        margin-bottom: 2px
        &__header
          padding: 0 15px !important
      .dropdown-menu.show
        z-index: 2
        transform: translate3d(32px, 28px, 0) !important
      .dropdown-toggle:focus
        box-shadow: none

  ::v-deep.date-time-label
    color: var(--color-primary)

  .manage-fonts_16px, .manage-fonts_18px
    .commutator-details
      ::v-deep.dropdown-menu.show
        transform: translate3d(36px, 28px, 0) !important

  @media print
    .commutator-details
      display: block

  @media screen
    +media-md()
      .commutator-details
        &__title
          min-width: 16vw
        &__maintenance-date
          min-width: 48vw
    +media-lg()
      .commutator-details
        &__title
          min-width: 12vw
        &__maintenance-date
          min-width: 36vw
    +media-max-sm()
      .commutator-details
        &__maintenance-icon
          min-width: 3.5vw
          margin-right: 5px
    +media-max-lg()
      .commutator-details
        &__body-maintenance,
        &__body-references
          flex: 0 0 100%
          max-width: 100%
        &__body-references
          padding-top: 0.5rem
          padding-left: 0 !important
</style>
